import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCommentAutomationLogs,
  type AutomationLog,
  type AutomationMisses,
  type LogStatusFilter,
} from "@/hooks/use-comment-automations";

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  sent: "default",
  failed: "destructive",
  skipped: "secondary",
  pending: "outline",
  gated: "outline",
};

const STATUS_FILTERS: LogStatusFilter[] = ["sent", "failed", "skipped", "pending", "gated"];

export interface AutomationLogsDrawerProps {
  brandId: string;
  automationId: string | null;
  automationName?: string;
  onOpenChange: (open: boolean) => void;
}

export function AutomationLogsDrawer({
  brandId,
  automationId,
  automationName,
  onOpenChange,
}: AutomationLogsDrawerProps) {
  const { t } = useTranslation("inbox");
  const [status, setStatus] = useState<LogStatusFilter>("all");
  // Filtering server-side: the drawer holds one page of 50, so filtering it
  // locally would hide matches that fall outside that page — selecting
  // "Failed" could read as "no failures" when older ones exist.
  const logs = useCommentAutomationLogs(brandId, automationId, status);
  const rows = logs.data?.logs ?? [];
  const misses = logs.data?.misses ?? null;

  return (
    <Sheet open={Boolean(automationId)} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg w-full flex flex-col gap-0">
        <SheetHeader>
          <SheetTitle className="text-sm font-semibold">
            {t("automations.logs.title")}
          </SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">
            {automationName ?? ""}
          </SheetDescription>
        </SheetHeader>

        <div className="py-3">
          <Select value={status} onValueChange={(v) => setStatus(v as LogStatusFilter)}>
            <SelectTrigger className="w-40" aria-label={t("automations.logs.filterAll")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("automations.logs.filterAll")}</SelectItem>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`automations.logs.status.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {misses && (misses.total ?? 0) > 0 && <MissesPanel misses={misses} />}
          {logs.isLoading ? (
            <>
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-xs text-muted-foreground">
              {t("automations.logs.empty")}
            </p>
          ) : (
            rows.map((log) => <LogRow key={log.id} log={log} />)
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function LogRow({ log }: { log: AutomationLog }) {
  const { t } = useTranslation("inbox");
  const pe = log.platformError;
  return (
    <div className="rounded-md border border-border p-2.5 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-medium truncate">
          {log.commenterName ?? log.commenterId ?? "—"}
        </span>
        <div className="flex items-center gap-1">
          {log.source === "dm" && (
            <Badge variant="outline">{t("automations.logs.sourceDm")}</Badge>
          )}
          <Badge variant={STATUS_VARIANT[log.status] ?? "secondary"}>
            {t(`automations.logs.status.${log.status}`, { defaultValue: log.status })}
          </Badge>
        </div>
      </div>
      {log.commentText && (
        <p className="text-xs text-muted-foreground line-clamp-2">{log.commentText}</p>
      )}
      {log.diagnosis && (
        <p
          role="note"
          className="flex gap-1.5 rounded bg-destructive/10 px-2 py-1.5 text-xs text-destructive"
        >
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          {t(`automations.logs.diagnosis.${log.diagnosis}`)}
        </p>
      )}
      {log.error && <p className="text-xs text-destructive">{log.error}</p>}
      {(pe || log.privateReplyConsumed) && (
        <p className="text-[11px] text-muted-foreground">
          {pe &&
            t("automations.logs.platformError", {
              code: pe.code ?? "?",
              subcode: pe.subcode ?? "—",
            })}
          {pe && log.privateReplyConsumed ? " · " : ""}
          {log.privateReplyConsumed && t("automations.logs.privateReplyConsumed")}
        </p>
      )}
      <p className="text-[11px] text-muted-foreground">
        {log.createdAt ? new Date(log.createdAt).toLocaleString() : ""}
      </p>
    </div>
  );
}

function MissesPanel({ misses }: { misses: AutomationMisses }) {
  const { t } = useTranslation("inbox");
  return (
    <div className="rounded-md border border-dashed border-border p-2.5 space-y-1.5">
      <p className="text-[13px] font-medium">
        {t("automations.logs.misses.title", {
          count: misses.total ?? 0,
          days: misses.retentionDays ?? 0,
        })}
      </p>
      <p className="text-xs text-muted-foreground">{t("automations.logs.misses.hint")}</p>
      {(misses.samples ?? []).map((m, i) => (
        <div key={i} className="text-xs">
          <span className="font-medium">{m.commenterName ?? "—"}</span>
          {": "}
          <span className="text-muted-foreground">{m.commentText ?? ""}</span>
          {m.excludedBy && (
            <Badge variant="outline" className="ml-1.5">
              {t("automations.logs.misses.excludedBy", { reason: m.excludedBy })}
            </Badge>
          )}
        </div>
      ))}
    </div>
  );
}
